import { useTranslation } from 'react-i18next';
import { AlertTriangle, PlugZap } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import type { PlatformSettings } from './types';

type Props = {
  settings: PlatformSettings;
};

/**
 * Says why the page is empty when it is: env not configured for this tenant,
 * or crypto-api not answering. Without it, "no wallets, no merchants" reads as
 * "nothing set up yet" when the real answer is "the backend cannot talk to
 * crypto-api".
 */
export function AvailabilityBanner({ settings }: Props) {
  const { t } = useTranslation('manageCryptoPayments');

  if (settings.configured && settings.reachable) return null;

  const isUnreachable = settings.configured && !settings.reachable;

  return (
    <Alert variant={isUnreachable ? 'destructive' : 'warning'}>
      {isUnreachable ? <PlugZap className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      <AlertTitle>
        {isUnreachable ? t('availability.unreachableTitle') : t('availability.notConfiguredTitle')}
      </AlertTitle>
      <AlertDescription>
        <p>{isUnreachable ? t('availability.unreachable') : t('availability.notConfigured')}</p>
        {settings.apiUrl && (
          <p className="mt-1 text-xs">
            {t('availability.apiUrl')}: <span className="font-mono">{settings.apiUrl}</span>
          </p>
        )}
        {settings.error && <p className="mt-1 break-all font-mono text-xs">{settings.error}</p>}
      </AlertDescription>
    </Alert>
  );
}
